import { ref, toValue, computed, type MaybeRefOrGetter, type Ref, type ComputedRef } from 'vue';
import Fuse, { type IFuseOptions} from 'fuse.js';

type FuzzySearchReturn<T> = {
    searchString: Ref<string>;
    filteredItems: ComputedRef<T[]>;
};

/**
 * Fuzzy search composable using Fuse.js
 * @function useFuzzySearch
 * @param items - The items to search through
 * @param options - Optional fuse.js options
 * @returns Object containing search string and filtered items
 */

export function useFuzzySearch<T>(
    items: MaybeRefOrGetter<T[]>,
    options: Partial<IFuseOptions<T>> = {}
): FuzzySearchReturn<T> {
    const defaultOptions: IFuseOptions<T> = {
        threshold: 0.3,
        findAllMatches: true,
        ignoreLocation: true,
        keys: ['display']
    };
    const searchString = ref<string>('');
    const fuseInstance = computed(() => {
        const itemsValue = toValue(items) ?? [];
        return new Fuse(itemsValue, { ...defaultOptions, ...options });
    });

    const filteredItems = computed(() => {
        if (searchString.value !== '') {
            return fuseInstance.value.search(searchString.value).map(result => result.item);
        }
        return toValue(items) ?? [];
    });

    return {
        searchString,
        filteredItems
    };
}
